"use client";
/**
 * Media-led: the demonstration is the card.
 *
 * This is the composition for a row whose clip has actually been resolved. The
 * frame wears the clip's own measured ratio, the poster and the player share
 * one box so nothing reflows when a reader presses play, and the caption under
 * it carries the one thing the picture cannot say — which shoot it came off,
 * or why there is nothing to press.
 *
 * It is not the default, and the reason is in the capture rather than in taste:
 * two of the three catalog rows on the mounted page publish `hasVideo: true`
 * and no address, so opening on this composition opens on two designed
 * absences.
 */
import type { ReactElement } from "react";
import {
  CresultcardIdentity,
  CresultcardStack,
  CRC_TILE_INSET,
  CresultcardDensity,
  CresultcardIdentitySlot,
  CresultcardMediaScale,
  CresultcardStackDensity,
  CresultcardTilePadding,
  CresultcardTitleLines,
} from "@/components/ui/Cresultcard";
import {
  C_WORKOUT_CARD_MODIFIER,
} from "./cworkoutcard.constants";
import {
  CworkoutcardMetaLine,
  CworkoutcardMuscleLine,
  CworkoutcardTypeChips,
  CworkoutcardVideoCaption,
} from "./CworkoutcardMeta";
import { CworkoutcardShell } from "./CworkoutcardShell";
import {
  CworkoutcardVideo,
  useCworkoutcardVideoState,
} from "./CworkoutcardVideo";
import {
  CworkoutcardVariant,
  type CworkoutcardVariantProps,
} from "./cworkoutcard.types";

const CARD_INSET = CRC_TILE_INSET[CresultcardTilePadding.Regular];
const CARD_TRUNCATION: readonly CresultcardIdentitySlot[] = [
  CresultcardIdentitySlot.Title,
];

export function CworkoutcardDemoFrame({
  data,
  openControl,
}: CworkoutcardVariantProps): ReactElement {
  const state = useCworkoutcardVideoState(data);

  return (
    <CworkoutcardShell
      density={CresultcardDensity.Card}
      head={
        <CworkoutcardVideo
          data={data}
          scale={CresultcardMediaScale.Face}
        />
      }
      modifier={C_WORKOUT_CARD_MODIFIER[CworkoutcardVariant.DemoFrame]}
      openable
      variant={CworkoutcardVariant.DemoFrame}
      body={
        <div
          style={{ minWidth: 0, padding: CARD_INSET }}
        >
          <CresultcardStack
            density={CresultcardStackDensity.Snug}
          >
            <CresultcardIdentity
              openControl={openControl}
              secondary={
                <CworkoutcardMetaLine
                  data={data}
                />
              }
              title={data.title}
              titleLines={CresultcardTitleLines.Two}
              truncationOrder={CARD_TRUNCATION}
            />
            <CworkoutcardVideoCaption
              data={data}
              state={state}
            />
            <CworkoutcardTypeChips
              data={data}
            />
          </CresultcardStack>
        </div>
      }
      foot={
        <div
          style={{
            minWidth: 0,
            paddingBlock: CARD_INSET,
            paddingInline: CARD_INSET,
          }}
        >
          <CworkoutcardMuscleLine
            data={data}
          />
        </div>
      }
    />
  );
}
